import { Uri } from "@baseline/common/uri";
import { Context } from "./context";
import { Request } from "./request";
import { Route } from "./route";
import { RoutePathArguments } from "./route-path";


export class Router<C extends Context = Context>
{
    constructor(
        readonly routes: Route[]
    )
    {
        this.route = this.route.bind(this);
    }

    async route(context: C): Promise<boolean>
    {
        for (let i = 0; i < this.routes.length; i++)
        {
            const processed = await this.processRoute(this.routes[i], context);
            if (processed)
                return true;
        }

        return false;
    };


    protected async processRoute(route: Route, context: C): Promise<boolean>
    {
        const { request } = context;

        // Verify predicates
        let args: RoutePathArguments;
        let segments: string[];
        if (route.path)
        {
            // Verify path
            const result = await route.path.match(request.requestSegments);
            if (!result)
                return false;
            [args, segments] = result;
            if (route.endPoint && segments.length < request.requestSegments.length)
                return false;
        }
        else if (route.test)
        {
            // Verify test predicate
            const result = await route.test(context);
            if (!result)
                return false;
        }
        else if (route.endPoint && request.requestSegments.length > 0)
        {
            return false;
        }

        // Create inner context
        const innerContext = this.createInnerContext(context, segments ?? [], args ?? {});

        // Handle
        const handling = route.handlers.map(handler => handler(innerContext));
        const handled = await Promise.all(handling);
        return handled.some(result => result !== false);
    }

    protected createInnerContext(parent: C, routeSegments: string[], routeArgs: RoutePathArguments): C
    {
        const request = this.createInnerRequest(parent.request, routeSegments, routeArgs);
        const context = { ...parent, request, parent };
        return context;
    }

    protected createInnerRequest(parent: Request, routeSegments: string[], routeArgs: RoutePathArguments): Request
    {
        let { basePath, baseSegments, requestSegments, requestPath, args } = parent;

        baseSegments = [...(parent.baseSegments ?? []), ...routeSegments];
        basePath = Uri.join(...baseSegments);
        requestSegments = parent.requestSegments.slice(routeSegments.length);
        requestPath = Uri.join(...requestSegments);
        args = { ...parent.args, ...routeArgs };

        const request: Request = { ...parent, basePath, baseSegments, requestPath, requestSegments, args };
        return request;
    }
}
